import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { financeNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Camera, Upload, X, Wallet, TrendingUp, Loader2, ImageIcon, CheckCircle2 } from 'lucide-react';
import api, { fmtIDR, API } from '@/lib/api';
import { toast } from 'sonner';
import { EXPENSE_CATEGORIES, FINANCE_ONLY_CATEGORIES } from '@/lib/expenseCategories';
import { compressImageFile, estimateBase64Size } from '@/lib/imageCompress';

export default function FinanceOperational() {
  const [balance, setBalance] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupSubmitting, setTopupSubmitting] = useState(false);

  // Expense form state
  const [cat, setCat] = useState('logistik');
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receipt, setReceipt] = useState(null); // data URL
  const [receiptSize, setReceiptSize] = useState(0);
  const [compressing, setCompressing] = useState(false);
  const [expSubmitting, setExpSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const loadBalance = async () => {
    try {
      const { data } = await api.get('/cash/balance');
      setBalance(data);
    } catch (e) { console.error('[FinanceOperational] gagal load balance:', e); }
  };
  useEffect(() => { loadBalance(); }, []);

  const doTopup = async (e) => {
    e.preventDefault();
    const n = Number(topupAmount);
    if (!n || n <= 0) return toast.error('Jumlah topup tidak valid');
    setTopupSubmitting(true);
    try {
      const { data } = await api.post('/cash/topup', { amount: n, note: topupNote || null });
      toast.success(`Kas ditambah ${fmtIDR(n)}. Saldo sekarang ${fmtIDR(data.balance)}`);
      setTopupAmount('');
      setTopupNote('');
      loadBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal topup');
    } finally { setTopupSubmitting(false); }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('File harus berupa gambar');
    setCompressing(true);
    try {
      const dataUrl = await compressImageFile(file, { maxDim: 1200, quality: 0.75 });
      setReceipt(dataUrl);
      setReceiptSize(estimateBase64Size(dataUrl));
      toast.success('Foto bukti siap');
    } catch (e) {
      toast.error('Gagal memproses foto');
    } finally { setCompressing(false); }
  };

  const doExpense = async (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!n || n <= 0) return toast.error('Jumlah pengeluaran tidak valid');
    if (!recipient.trim()) return toast.error('Nama penerima wajib diisi');
    if (!receipt) {
      // Warn but still allow (with confirmation)
      if (!window.confirm('Anda belum mengunggah foto bukti pengeluaran. Sesuai kebijakan, semua pengeluaran WAJIB disertai bukti. Lanjutkan tanpa bukti?')) {
        return;
      }
    }
    setExpSubmitting(true);
    try {
      const { data } = await api.post('/cash/expenses', {
        category: cat,
        recipient_name: recipient.trim(),
        amount: n,
        description: description.trim() || null,
        receipt_photo: receipt,
      });
      toast.success(`Pengeluaran tercatat. Saldo tersisa ${fmtIDR(data.balance)}`);
      setAmount('');
      setRecipient('');
      setDescription('');
      setReceipt(null);
      setReceiptSize(0);
      loadBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal simpan pengeluaran');
    } finally { setExpSubmitting(false); }
  };

  const catInfo = useMemo(() => EXPENSE_CATEGORIES.find((c) => c.value === cat), [cat]);
  const financeCategories = useMemo(() => EXPENSE_CATEGORIES.filter((c) => FINANCE_ONLY_CATEGORIES.includes(c.value)), []);

  return (
    <AppShell nav={financeNav} title="Biaya Operasional">
      <div className="space-y-6">
        {/* Balance strip */}
        <div className="rounded-xl border bg-gradient-to-r from-primary/10 via-background to-background p-4 flex items-center gap-4">
          <div className="h-11 w-11 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
            <Wallet className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">Saldo Kas Perusahaan Saat Ini</div>
            <div className="text-2xl font-heading font-semibold tabular-nums" data-testid="op-current-balance">{fmtIDR(balance?.balance || 0)}</div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-[10px] text-muted-foreground">Total masuk</div>
            <div className="text-sm font-medium text-emerald-700 tabular-nums">+{fmtIDR(balance?.total_topups || 0)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">Total keluar</div>
            <div className="text-sm font-medium text-rose-700 tabular-nums">-{fmtIDR(balance?.total_expenses || 0)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Topup */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" />Tambah Saldo Kas</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={doTopup} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Jumlah (Rupiah) <span className="text-rose-500">*</span></Label>
                  <Input
                    type="number" min="1" step="1" inputMode="numeric"
                    value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="mis. 5000000"
                    data-testid="topup-amount"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Catatan (opsional)</Label>
                  <Input
                    value={topupNote} onChange={(e) => setTopupNote(e.target.value)}
                    placeholder="mis. Setoran modal, tarik kas rekening BCA..."
                    data-testid="topup-note"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={topupSubmitting} data-testid="topup-submit">
                  {topupSubmitting ? 'Menambah...' : 'Tambah Saldo Kas'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Expense form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {catInfo?.icon && <catInfo.icon className={`h-4 w-4 ${catInfo.accent}`} />}
                Catat Pengeluaran
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={doExpense} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Kategori <span className="text-rose-500">*</span></Label>
                  <Select value={cat} onValueChange={setCat}>
                    <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {financeCategories.map((c) => (
                        <SelectItem key={c.value} value={c.value} data-testid={`expense-category-${c.value}`}>
                          <div className="flex items-center gap-2">
                            <c.icon className="h-3.5 w-3.5" />
                            {c.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Ditujukan Untuk (Nama) <span className="text-rose-500">*</span></Label>
                  <Input
                    value={recipient} onChange={(e) => setRecipient(e.target.value)}
                    placeholder={cat === 'kasbon_karyawan' ? 'Nama karyawan' : cat === 'logistik' ? 'Nama SPBU / vendor' : cat === 'perawatan_kendaraan' ? 'Nama bengkel' : 'Nama toko / warung'}
                    data-testid="expense-recipient"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Besar Pengeluaran (Rupiah) <span className="text-rose-500">*</span></Label>
                  <Input
                    type="number" min="1" step="1" inputMode="numeric"
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="mis. 250000"
                    data-testid="expense-amount"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Deskripsi (opsional)</Label>
                  <Textarea
                    rows={2}
                    value={description} onChange={(e) => setDescription(e.target.value)}
                    placeholder="Detail tambahan mengenai pengeluaran ini..."
                    data-testid="expense-description"
                  />
                </div>

                {/* WAJIB BUKTI notice */}
                <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                  <div className="text-xs text-amber-900">
                    <b>WAJIB:</b> Semua pengeluaran harus disertai <b>foto bukti</b> (nota, kwitansi, atau foto transaksi). Upload di bawah.
                  </div>
                </div>

                {/* Receipt upload */}
                <div className="space-y-2">
                  <Label>Foto Bukti Pengeluaran {!receipt && <span className="text-rose-500">*</span>}</Label>
                  {receipt ? (
                    <div className="relative rounded-lg border overflow-hidden">
                      <img src={receipt} alt="receipt preview" className="w-full max-h-64 object-contain bg-muted cursor-pointer" onClick={() => setShowPreview(true)} data-testid="receipt-preview" />
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button type="button" onClick={() => setShowPreview(true)} className="rounded-full bg-black/60 text-white p-1.5 hover:bg-black/80" title="Perbesar">
                          <ImageIcon className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => { setReceipt(null); setReceiptSize(0); }} className="rounded-full bg-rose-600 text-white p-1.5 hover:bg-rose-700" title="Hapus foto" data-testid="receipt-remove">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="absolute bottom-2 left-2 flex items-center gap-1">
                        <Badge className="bg-emerald-600 text-white border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Foto siap ({Math.round(receiptSize/1024)} KB)</Badge>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer hover:bg-accent transition-colors">
                      {compressing ? (
                        <><Loader2 className="h-5 w-5 animate-spin" />Memproses foto...</>
                      ) : (
                        <>
                          <Camera className="h-5 w-5 text-muted-foreground" />
                          <div className="text-sm">
                            <div className="font-medium">Klik untuk pilih foto</div>
                            <div className="text-[10px] text-muted-foreground">JPG/PNG, otomatis dikompres</div>
                          </div>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handleFileSelect(e.target.files?.[0])}
                        data-testid="receipt-file-input"
                        disabled={compressing}
                      />
                    </label>
                  )}
                </div>

                <Button type="submit" className="w-full bg-rose-600 hover:bg-rose-700" disabled={expSubmitting} data-testid="expense-submit">
                  {expSubmitting ? 'Menyimpan...' : (<><Upload className="h-4 w-4 mr-2" />Catat Pengeluaran</>)}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Preview Foto Bukti</DialogTitle></DialogHeader>
          {receipt && <img src={receipt} alt="receipt full" className="w-full max-h-[75vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

// Small badge helper (inline to avoid import churn)
function Badge({ children, className = '' }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${className}`}>{children}</span>;
}
